/**
 * Custom Variant Logic for Refit Global
 * Advanced JS Controller for: Lowest Price Selection, Smart Fallback, and Condition Price Sync.
 * Interacts with 'variant-selects' component and 'pubsub' event system.
 */

class RefitVariantController {
    constructor() {
        // Only apply to main-product template
        if (!window.isMainProduct) return;
        this.selectors = {
            component: 'variant-selects',
            fieldsets: 'fieldset',
            swatchValue: '[data-selected-swatch-value]',
            gradeLegendMatch: /condition|grade/i
        };

        this.component = document.querySelector(this.selectors.component);
        if (!this.component) return;

        this.isProcessing = false;
        this.init();
    }

    init() {
        // 1. Initial Logic: Select Lowest Price Variant on Page Load
        this.runInitially();

        // 2. Hook into theme's custom pubsub system
        if (typeof subscribe === 'function') {
            subscribe(PUB_SUB_EVENTS.variantChange, (data) => {
                this.handleCoreVariantChange(data);
            });
        }

        // 3. Listen for manual option changes to trigger smart fallback
        this.component.addEventListener('change', (e) => this.onOptionSelectionChange(e));

        // 4. Global UI Sync
        this.syncAllCustomUI();
    }

    runInitially() {
        const urlParams = new URLSearchParams(window.location.search);
        const urlVariantId = urlParams.get('variant');

        const startSync = () => {
            const variants = this.getVariantData();
            if (urlVariantId && variants) {
                const selectedVariant = variants.find(v => v.id.toString() === urlVariantId);
                // If variant from URL is out of stock, force selection of the cheapest available one
                if (selectedVariant && !selectedVariant.available) {
                    this.selectLowestPrice();
                }
            } else if (!urlVariantId) {
                this.selectLowestPrice();
            }

            this.syncAllCustomUI();

            // Re-sync after a few more frames to catch late rendering elements
            setTimeout(() => this.syncAllCustomUI(), 500);
            setTimeout(() => this.syncAllCustomUI(), 1500);
        };

        if (document.readyState === 'complete') {
            setTimeout(startSync, 200);
        } else {
            window.addEventListener('load', startSync);
        }
    }

    /**
     * Logic to find and apply the lowest priced available variant.
     */
    selectLowestPrice() {
        const variants = this.getVariantData();
        if (!variants) return;

        const cheapest = variants.reduce((prev, curr) => {
            if (curr.available && (!prev || curr.price < prev.price)) return curr;
            return prev;
        }, null);

        if (cheapest) {
            this.applyVariant(cheapest);
        }
    }

    /**
     * Triggered when a physical radio/select is changed. Handles Smart Fallback.
     */
    onOptionSelectionChange(e) {
        if (this.isProcessing) return;

        const currentVariant = this.component.currentVariant;

        // Smart Fallback: If selection is unavailable, jump to something that IS available.
        if (!currentVariant || !currentVariant.available) {
            this.processSmartFallback(e);
        }

        this.syncAllCustomUI();
    }

    /**
     * Triggered after theme finishes rendering product info.
     */
    handleCoreVariantChange(data) {
        this.syncAllCustomUI();
    }

    /**
     * Fallback Algorithm: Prioritizes maintaining the newly selected option.
     */
    processSmartFallback(e) {
        const variants = this.getVariantData();
        const currentOptions = this.getCurrentOptions();
        const changedIndex = this.getChangedIndex(e);
        const condIndex = this.getConditionIndex();

        this.isProcessing = true;

        try {
            const targetVal = currentOptions[changedIndex];
            const condIdx = this.getConditionIndex();
            const currentGrade = currentOptions[condIdx];

            // Tier 1: Match Clicked Option AND Current Grade
            let pool = variants.filter(v => v.available && v.options[changedIndex] === targetVal && v.options[condIdx] === currentGrade);

            // Tier 2: Match Clicked Option (Any Grade)
            if (pool.length === 0) {
                pool = variants.filter(v => v.available && v.options[changedIndex] === targetVal);
            }

            // Tier 3: Any Available in Current Grade
            if (pool.length === 0) {
                pool = variants.filter(v => v.available && v.options[condIdx] === currentGrade);
            }

            // Tier 4: Absolutely any available variant
            if (pool.length === 0) {
                pool = variants.filter(v => v.available);
            }

            let winner = null;
            if (pool.length > 0) {
                winner = pool.reduce((best, curr) => {
                    let bestScore = this.calculateOverlap(best.options, currentOptions, changedIndex);
                    let currScore = this.calculateOverlap(curr.options, currentOptions, changedIndex);
                    return currScore > bestScore ? curr : best;
                }, pool[0]);
            }

            if (winner) {
                this.applyVariant(winner);
            }
        } finally {
            this.isProcessing = false;
        }
    }

    /**
     * Syncs specialized UI elements: Grade Prices, Warranty, EMI, and Headers.
     */
    syncAllCustomUI() {
        let variant = this.component.currentVariant;

        // Fallback: If theme hasn't set currentVariant yet, find it manually
        if (!variant) {
            try {
                const options = this.getCurrentOptions();
                const data = this.getVariantData();
                if (data && options) {
                    variant = data.find(v => v.options.every((opt, i) => opt === options[i]));
                }
            } catch (e) { }
        }

        if (!variant) return;

        this.updateConditionButtonPrices();
        this.updateWarrantyInfo(variant);
        this.updateSpecialHeaders();
        this.updatePrepaidPrice(variant);

        // Trigger EMI update if method exists in global component
        if (typeof this.component.emiCalculation === 'function') {
            this.component.emiCalculation();
        }
        if (typeof this.component.Ram_Rom === 'function') {
            this.component.Ram_Rom();
        }
    }

    updatePrepaidPrice(variant) {
        if (!variant || !variant.price) return;
        const rawPrice = variant.price;
        const discount = Math.round(rawPrice * 0.03);
        const prepaidPrice = Math.round((rawPrice - discount) / 100);

        const formattedNumber = prepaidPrice.toLocaleString('en-IN', {
            maximumFractionDigits: 0,
            minimumFractionDigits: 0
        });

        const salePriceEls = document.querySelectorAll('.product__info-container .price-item--sale, .no-js-hidden[id^="price-"] .price-item--sale, .price-savings .price-item--sale');
        salePriceEls.forEach(el => {
            if (el.textContent.includes('Rs.')) {
                el.textContent = 'Rs. ' + formattedNumber;
            } else if (el.textContent.includes('₹')) {
                el.textContent = '₹' + formattedNumber;
            } else {
                el.textContent = 'Rs. ' + formattedNumber;
            }
        });
    }

    updateConditionButtonPrices() {
        const variants = this.getVariantData();
        const currentOpts = this.getCurrentOptions();
        const condIdx = this.getConditionIndex();

        const conditions = [...new Set(variants.map(v => v.options[condIdx]))];

        conditions.forEach(cond => {
            const idealSelection = [...currentOpts];
            idealSelection[condIdx] = cond;

            const match = variants.find(v => v.available && v.options.every((opt, i) => opt === idealSelection[i])) ||
                variants.filter(v => v.available && v.options[condIdx] === cond)[0];

            if (match) {
                const formattedPrice = (match.price / 100).toLocaleString('en-IN');
                this.updatePriceLabel(cond, formattedPrice);
            }
        });
    }

    updatePriceLabel(condition, price) {
        const selectors = [
            `.variant-price-${condition.replace(/[\s\.]+/g, '-')}`,
            `.variant-price-${condition.replace(/\s+/g, '.')}`,
            `.variant-price-${condition.replace(/[\s\.]+/g, '')}`,
            `.variant-price-${condition.replace(/\./g, '\\.')}`
        ];

        try {
            const els = document.querySelectorAll(selectors.join(','));
            els.forEach(el => {
                let cleanPrice = price.toString().replace(/[₹\s,]/g, '').trim();
                const num = parseFloat(cleanPrice);
                if (!isNaN(num)) {
                    el.textContent = num.toLocaleString('en-IN');
                } else {
                    el.textContent = cleanPrice;
                }
            });
        } catch (e) { }
    }

    updateWarrantyInfo(variant) {
        const isLaptopContext = document.querySelector('#media-warranty-badge[data-laptop-context="true"]') !== null;
        if (isLaptopContext) return;

        const isPreOwnedContext = document.querySelector('media-gallery[data-preowned-context="true"]') !== null;

        const condIdx = this.getConditionIndex();
        const cond = (variant.options[condIdx] || '').toLowerCase();
        const isSuperb = cond.includes('superb');
        const isPreOwnedCondition = cond.includes('pre-owned') || cond.includes('preowned');

        const warrantyMonths = (isSuperb || isPreOwnedContext || isPreOwnedCondition) ? "12" : "6";
        const suffix = isPreOwnedContext ? "100% Original Devices" : "";
        const fullWarrantyText = suffix ? `${warrantyMonths} Months Warranty + ${suffix}` : `${warrantyMonths} Months Warranty`;

        const updateTargets = [
            '.warranty-text-content',
            '#media-warranty-badge',
            '.warranty-highlight',
            '.warranty-feature-card__title'
        ];

        document.querySelectorAll(updateTargets.join(',')).forEach(el => {
            if (el.id === 'media-warranty-badge') {
                const num = el.querySelector('.warranty-number');
                const txt = el.querySelector('.warranty-text');
                if (num) num.textContent = warrantyMonths;
                if (txt) txt.textContent = 'Months Warranty';
            } else if (el.tagName === 'H5' || el.classList.contains('multicolumn-card__title')) {
                if (el.textContent.includes('Warranty')) {
                    el.innerHTML = suffix ? `Up to ${warrantyMonths} Months Warranty & ${suffix}` : `Up to ${warrantyMonths} Months Warranty`;
                }
            } else if (el.classList.contains('warranty-feature-card__title')) {
                el.textContent = `${warrantyMonths} Months Warranty`;
            } else {
                el.textContent = fullWarrantyText;
            }
        });

        // Update box images
        const gallery = document.querySelector('media-gallery');
        const boxImgs = document.querySelectorAll('#whats-in-box-image, #whats-in-box-thumbnail');

        if (gallery && boxImgs.length > 0) {
            let boxUrl = '';
            if (isPreOwnedContext) {
                boxUrl = gallery.getAttribute('data-preowned-url');
            } else {
                if (isSuperb) {
                    boxUrl = gallery.getAttribute('data-superb-url');
                } else if (cond.includes('very good')) {
                    boxUrl = gallery.getAttribute('data-very-good-url');
                } else {
                    boxUrl = gallery.getAttribute('data-good-url');
                }
            }
            if (boxUrl) {
                boxImgs.forEach(img => { img.src = boxUrl; });
            }
        }

        // Update specific text spans and captions
        const textElements = document.querySelectorAll('.caption, .grid-item, .trust-icon-text, span, p');
        textElements.forEach(el => {
            if (el.children.length === 0 && (el.textContent.includes('6 Months') || el.textContent.includes('12 Months'))) {
                if (el.textContent.includes('Warranty') || el.textContent.includes('Screen Protection')) {
                    el.textContent = el.textContent.replace(/(6|12)\s*Months/g, `${warrantyMonths} Months`);
                }
            }
        });

        this.updateDynamicWarrantyContainer(isSuperb);
    }

    updateDynamicWarrantyContainer(isSuperb) {
        const isPreOwnedContext = document.querySelector('media-gallery[data-preowned-context="true"]') !== null;
        const warrantyMonths = (isSuperb || isPreOwnedContext) ? '12' : '6';
        const suffix = isPreOwnedContext ? '100% Original Devices' : '';
        const warrantyText = suffix ? `${warrantyMonths} Months Warranty + ${suffix}` : `${warrantyMonths} Months Warranty`;
        const gradeVariantGroup = document.querySelector('.accordion-contentCondition');
        if (!gradeVariantGroup) return;

        let warrantyContainer = gradeVariantGroup.querySelector('.dynamic-warranty-container');

        if (!warrantyContainer) {
            warrantyContainer = document.createElement('div');
            warrantyContainer.className = 'dynamic-warranty-container';
            warrantyContainer.style.cssText = 'display:flex; align-items:center; gap:6px; padding:4px 8px; border-radius:6px; background: #fff; border: 1px solid #f0f0f0; font-size:12px; line-height:1.4; white-space:nowrap; align-self:flex-start; margin-top:8px; width: fit-content;';

            const img = document.createElement('img');
            img.src = 'https://cdn.shopify.com/s/files/1/0874/4068/7387/files/099082977d1b4f228be259f3cc82ab6a.avif?v=1762844375';
            img.width = 16;
            img.height = 16;
            img.style.cssText = 'object-fit:contain; width:16px; height:16px;';

            const span = document.createElement('span');
            span.className = 'dynamic-warranty-text';
            span.style.color = '#555';
            span.textContent = warrantyText;

            warrantyContainer.appendChild(img);
            warrantyContainer.appendChild(span);
            gradeVariantGroup.appendChild(warrantyContainer);
        } else {
            const span = warrantyContainer.querySelector('.dynamic-warranty-text');
            if (span) span.textContent = warrantyText;
        }
    }

    updateSpecialHeaders() {
        const options = this.getCurrentOptions();
        this.component.querySelectorAll('fieldset').forEach((fs, i) => {
            const headerVal = fs.querySelector(this.selectors.swatchValue);
            if (headerVal && options[i]) {
                headerVal.textContent = options[i];
            }
        });
    }

    getVariantData() {
        if (this.component.variantData) return this.component.variantData;
        const script = this.component.querySelector('[type="application/json"]');
        if (script) {
            try {
                this.component.variantData = JSON.parse(script.textContent);
                return this.component.variantData;
            } catch (e) {
                return null;
            }
        }
        return null;
    }

    getCurrentOptions() {
        return Array.from(this.component.querySelectorAll('select, fieldset'), (el) => {
            if (el.tagName === 'SELECT') return el.value;
            return Array.from(el.querySelectorAll('input')).find(i => i.checked)?.value;
        });
    }

    getConditionIndex() {
        if (this._condIdx !== undefined) return this._condIdx;
        const legends = Array.from(this.component.querySelectorAll('legend'));
        const idx = legends.findIndex(l => this.selectors.gradeLegendMatch.test(l.textContent));
        this._condIdx = idx === -1 ? 0 : idx;
        return this._condIdx;
    }

    getChangedIndex(e) {
        if (!e || !e.target) return -1;
        const picker = e.target.closest('fieldset, select');
        return Array.from(this.component.querySelectorAll('fieldset, select')).indexOf(picker);
    }

    calculateOverlap(optsA, optsB, ignoreIdx) {
        let score = 0;
        optsA.forEach((val, i) => {
            if (i === ignoreIdx) return;
            if (val === optsB[i]) {
                const type = this.getOptionType(i);
                if (type === 'color') score += 10;
                else if (type === 'size') score += 5;
                else score += 1;
            }
        });
        return score;
    }

    getOptionType(index) {
        const legends = Array.from(this.component.querySelectorAll('legend'));
        const text = legends[index]?.textContent.toLowerCase() || '';
        if (text.includes('color')) return 'color';
        if (text.includes('size') || text.includes('storage') || text.includes('ram')) return 'size';
        return 'other';
    }

    applyVariant(variant) {
        if (!variant) return;
        this.isProcessing = true;
        try {
            let lastModifiedEl = null;
            variant.options.forEach((val, i) => {
                const fs = this.component.querySelectorAll('fieldset')[i];
                if (fs) {
                    const input = fs.querySelector(`input[value="${val}"]`);
                    if (input) {
                        if (!input.checked) {
                            input.checked = true;
                            lastModifiedEl = input;
                        }
                    }
                } else {
                    const sel = this.component.querySelectorAll('select')[i];
                    if (sel && sel.value !== val) {
                        sel.value = val;
                        lastModifiedEl = sel;
                    }
                }
            });

            if (typeof this.component.onVariantChange === 'function') {
                this.component.onVariantChange({ target: lastModifiedEl || this.component.querySelector('input:checked') });
            } else {
                this.component.dispatchEvent(new Event('change', { bubbles: true }));
            }
        } finally {
            this.isProcessing = false;
        }
    }
}

// Bootstrapping
(function () {
    const start = () => {
        if (document.querySelector('variant-selects')) {
            window.customVariantLogic = new RefitVariantController();
        }
    };
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start);
    } else {
        start();
    }
})();
